import { create } from 'zustand';
import { isApiRequestError, type ClientErrorCode } from '@/api/client';
import { sound, type SoundName } from '@/fx/sound';

/**
 * Toasts carry message ids, never text (texts come from messages/*).
 * Errors: toast.error(e) → title "errors.<CODE>" (or common.error.network for NETWORK).
 * Sound (only when the player enabled it): errors → soft `common/error`, the rest → `common/notify`;
 * `sound` overrides it (`false` when the caller plays its own, e.g. the level-up fanfare).
 * On a game screen notices stay silent (chat spec: nothing disturbs a round); errors still sound — they answer the
 * player's own action.
 */
export type ToastKind = 'error' | 'notice' | 'success';

export interface ToastItem {
  id: number;
  kind: ToastKind;
  titleId: string;
  textId?: string;
  values?: Record<string, string | number>;
  /** an in-app link under the text (e.g. «your table is ready» → the table) */
  link?: { to: string; labelId: string };
  ttl: number;
}

export type ToastInput = Omit<ToastItem, 'id' | 'ttl'> & { ttl?: number; sound?: SoundName | false };

const TOAST_SOUND: Record<ToastKind, SoundName> = { error: 'common/error', notice: 'common/notify', success: 'common/notify' };

/** A game screen is open (GameShell marks its frame with `data-game`). */
function onGameScreen(): boolean {
  return typeof document !== 'undefined' && !!document.querySelector('[data-game]');
}

interface ToastState {
  items: ToastItem[];
  push(t: ToastInput): number;
  dismiss(id: number): void;
}

let seq = 1;

export const useToasts = create<ToastState>()((set) => ({
  items: [],
  push({ sound: snd, ...t }) {
    const id = seq++;
    const item: ToastItem = { ttl: 4500, ...t, id };
    const name = snd ?? (t.kind !== 'error' && onGameScreen() ? false : TOAST_SOUND[t.kind]);
    if (name) sound.play(name);
    set((s) => ({ items: [...s.items.slice(-3), item] }));
    return id;
  },
  dismiss: (id) => set((s) => ({ items: s.items.filter((x) => x.id !== id) })),
}));

export function errorMessageId(code: ClientErrorCode): string {
  return code === 'NETWORK' ? 'common.error.network' : `errors.${code}`;
}

/**
 * Errors with a designed screen (not enough tokens, limits, break…) are shown by features/errors
 * instead of a toast. The interceptor returns true when it took the error.
 */
type ErrorInterceptor = (e: unknown) => boolean;
let interceptor: ErrorInterceptor | null = null;
export function setErrorInterceptor(fn: ErrorInterceptor | null): void {
  interceptor = fn;
}

export const toast = {
  error(e: unknown): number {
    if (interceptor?.(e)) {
      // the designed error screen (not enough tokens, limits…) gets the same soft sound
      sound.play('common/error');
      return 0;
    }
    const code: ClientErrorCode = isApiRequestError(e) ? e.code : 'INTERNAL';
    const values = isApiRequestError(e) ? e.params : undefined;
    return useToasts.getState().push({ kind: 'error', titleId: errorMessageId(code), values });
  },
  code(code: ClientErrorCode, values?: Record<string, string | number>): number {
    return useToasts.getState().push({ kind: 'error', titleId: errorMessageId(code), values });
  },
  notice(titleId: string, textId?: string, values?: Record<string, string | number>): number {
    return useToasts.getState().push({ kind: 'notice', titleId, textId, values });
  },
  success(titleId: string, values?: Record<string, string | number>, o: { sound?: SoundName | false } = {}): number {
    return useToasts.getState().push({ kind: 'success', titleId, values, sound: o.sound });
  },
  /** a notice with a link (stays longer: the player has to get there) */
  link(
    titleId: string,
    link: { to: string; labelId: string },
    values?: Record<string, string | number>,
    textId?: string,
    o: { sound?: SoundName | false } = {},
  ): number {
    return useToasts.getState().push({ kind: 'notice', titleId, textId, values, link, ttl: 15_000, sound: o.sound });
  },
};
